import React, { Fragment, FC, useState } from 'react';
import { Alert, Box, Button, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { MaterialMacro, MaterialMacroMaterial } from 'models/material-macro';
import { LabMaterial } from 'models/material';
import { ApolloCache, Reference, useMutation, useQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import {
    getMaterialMacrosTemplate,
    deleteMaterialMacroTemplate,
    deleteMaterialMacroRowTemplate,
} from 'apollo/templates/material-macro';
import { getLabMaterialsTemplate } from 'apollo/templates/material';
import { useTranslation } from 'react-i18next';
import MaterialMacrosActions from './material-macros-actions.component';
import MaterialMacrosItem from './material-macros-item.component';
import AddMaterialMacroDialog from './add-material-macro-dialog/add-material-macro-dialog.component';
import EditMaterialMacroDialog from './edit-material-macro-dialog/edit-material-macro-dialog.component';
import AddMaterialMacroRowDialog from './add-material-macro-row-dialog/add-material-macro-row-dialog.component';
import Loader from 'components/shared/loader/loader.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';

const MaterialMacros: FC = () => {
    const { t: translate } = useTranslation(['lab-settings']);
    const [addMacroDialogOpen, setAddMacroDialogOpen] = useState(false);
    const [editMacroDialogOpen, setEditMacroDialogOpen] = useState(false);
    const [addRowDialogOpen, setAddRowDialogOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [materialMacro, setMaterialMacro] = useState<MaterialMacro>({} as MaterialMacro);
    const [materials, setMaterials] = useState<LabMaterial[]>([]);
    const [snackbarOpen, setSnackbarOpen] = useState({
        addedMacro: false,
        updatedMacro: false,
        deletedMacro: false,
    });
    const { showBoundary } = useDenthubErrorBoundary();

    const { loading: labMaterialsLoading } = useQuery(getLabMaterialsTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            filters: { active: true },
        },
        onCompleted({ labMaterials }) {
            setMaterials(labMaterials);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const { data: { materialMacros } = {}, loading: materialMacrosLoading } = useQuery(getMaterialMacrosTemplate, {
        onCompleted({ materialMacros }) {
            if (!materialMacro._id && materialMacros?.length) {
                setMaterialMacro(materialMacros[0]);
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [deleteMaterialMacro] = useMutation(deleteMaterialMacroTemplate, {
        variables: {
            materialMacroId: materialMacro._id,
        },
        onCompleted: ({ deleteMaterialMacro }) => {
            removeMaterialMacro(deleteMaterialMacro);
        },
        onError(error) {
            showBoundary(error);
        },
        update(cache: ApolloCache<MaterialMacro[]>) {
            cache.modify({
                fields: {
                    materialMacros(existingMacroRefs: Reference | (Reference | undefined)[], { readField }) {
                        if (existingMacroRefs instanceof Array) {
                            return [...existingMacroRefs].filter((macroRef: Reference | undefined) => {
                                return materialMacro._id !== readField('_id', macroRef);
                            });
                        }
                        return [];
                    },
                },
            });
            // Dropping it from the macros list above is not enough: every other cached
            // query that embeds this macro still holds a reference to the entity, so a
            // cache-first read keeps resolving it (the tendered action list showed a
            // deleted macro until a page refresh). Evicting the entity leaves those
            // references dangling, and Apollo filters dangling refs out of array fields
            // automatically - so every list drops it without needing its own update.
            cache.evict({ id: cache.identify({ __typename: 'MaterialMacro', _id: materialMacro._id }) });
            cache.gc();
        },
    });

    const [deleteMaterialMacroRow] = useMutation(deleteMaterialMacroRowTemplate, {
        onCompleted: ({ deleteMaterialMacroRow }) => {
            setMaterialMacro(deleteMaterialMacroRow);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleSelectedMaterialMacro = (selectedMaterialMacro: string) => {
        const macros = [...(materialMacros || [])];
        const foundMacro = macros.find(
            (macro: MaterialMacro) => macro.name.toLocaleLowerCase() === selectedMaterialMacro.toLocaleLowerCase(),
        );
        if (foundMacro) {
            setMaterialMacro(foundMacro);
        }
    };

    const addMaterialMacro = (macro: MaterialMacro) => {
        setMaterialMacro(macro);
        setSnackbarOpen({ ...snackbarOpen, addedMacro: true });
    };
    const editMaterialMacro = (macro: MaterialMacro) => {
        setMaterialMacro(macro);
        setSnackbarOpen({ ...snackbarOpen, updatedMacro: true });
    };
    const removeMaterialMacro = (materialMacroId: string) => {
        const macros = [...(materialMacros || [])];
        const index = macros.findIndex((macro: MaterialMacro) => macro._id?.toString() === materialMacroId.toString());
        if (index > -1) {
            macros.splice(index, 1);
        }
        setMaterialMacro((macros[0] as MaterialMacro) || ({} as MaterialMacro));
        setDeleteConfirmOpen(false);
        setSnackbarOpen({ ...snackbarOpen, deletedMacro: true });
    };

    const handleAddMaterialRow = (macro: MaterialMacro) => {
        setMaterialMacro(macro);
    };
    const handleRemoveMaterialRow = (material: MaterialMacroMaterial) => {
        deleteMaterialMacroRow({
            variables: {
                materialMacroId: materialMacro._id,
                materialId: material.material?._id,
            },
        });
    };

    return (
        <Fragment>
            {materialMacrosLoading && !labMaterialsLoading ? (
                <Loader />
            ) : (
                <Fragment>
                    <MaterialMacrosActions
                        onNewMaterialMacroClick={() => setAddMacroDialogOpen(true)}
                        materialMacros={materialMacros || []}
                        materialMacro={materialMacro}
                        onSelectedMaterialMacro={handleSelectedMaterialMacro}
                    />
                    {materialMacro.name && (
                        <Fragment>
                            <Box display="flex" alignItems="center">
                                <Box sx={{ cursor: 'pointer' }} onClick={() => setEditMacroDialogOpen(true)}>
                                    <Typography variant="h3">{materialMacro.name}</Typography>
                                </Box>
                                <Tooltip
                                    title={translate('lab-settings:REMOVE_MATERIAL_MACRO').toString()}
                                    placement="right"
                                >
                                    <IconButton
                                        sx={{ marginLeft: '4px' }}
                                        size="medium"
                                        onClick={() => setDeleteConfirmOpen(true)}
                                    >
                                        <DeleteIcon fontSize="medium" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Box mb={2} mt={1}>
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => setAddRowDialogOpen(true)}
                                >
                                    {translate('lab-settings:ADD_MATERIAL_MACRO_ROW')}
                                </Button>
                            </Box>
                            {materialMacro.materials?.length ? (
                                <Box mb={2}>
                                    <Paper variant="outlined">
                                        <MaterialMacrosItem
                                            materials={materialMacro.materials}
                                            onRemoveMaterialMacroRow={handleRemoveMaterialRow}
                                        />
                                    </Paper>
                                </Box>
                            ) : (
                                <Box padding={2}>
                                    <Alert severity="info">{translate('lab-settings:NO_MATERIAL_MACRO_ROWS')}</Alert>
                                </Box>
                            )}
                        </Fragment>
                    )}
                    <AddMaterialMacroDialog
                        dialogOpen={addMacroDialogOpen}
                        onCloseDialog={() => setAddMacroDialogOpen(false)}
                        onAddMaterialMacro={addMaterialMacro}
                        materialMacros={materialMacros || []}
                    />
                    <EditMaterialMacroDialog
                        materialMacro={materialMacro}
                        dialogOpen={editMacroDialogOpen}
                        onCloseDialog={() => setEditMacroDialogOpen(false)}
                        onEditMaterialMacro={editMaterialMacro}
                        materialMacros={materialMacros || []}
                    />
                    <AddMaterialMacroRowDialog
                        dialogOpen={addRowDialogOpen}
                        onCloseDialog={() => setAddRowDialogOpen(false)}
                        materialMacro={materialMacro}
                        materials={materials}
                        materialMacroId={materialMacro._id}
                        onAddMaterial={handleAddMaterialRow}
                    />
                    <ConfirmDialog
                        open={deleteConfirmOpen}
                        handleClose={() => setDeleteConfirmOpen(false)}
                        dialogContent={translate('lab-settings:DELETE_MATERIAL_MACRO_WARNING')}
                        handleConfirm={() => deleteMaterialMacro()}
                    />
                    {snackbarOpen.addedMacro ? (
                        <AlertSnackbar type="success" message={translate('lab-settings:MATERIAL_MACRO_ADDED')} />
                    ) : undefined}
                    {snackbarOpen.updatedMacro ? (
                        <AlertSnackbar type="success" message={translate('lab-settings:MATERIAL_MACRO_UPDATED')} />
                    ) : undefined}
                    {snackbarOpen.deletedMacro ? (
                        <AlertSnackbar type="success" message={translate('lab-settings:MATERIAL_MACRO_DELETED')} />
                    ) : undefined}
                </Fragment>
            )}
        </Fragment>
    );
};

export default MaterialMacros;
